import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { formatDate } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import tw from '../helpers/tw';
import { truncate } from '../utils/format';
import { asDateValue, field, recordAsMap } from '../utils/enriched-data';
import FlagButton from './flag-button.gts';
import posterUrl from '../helpers/poster-url';
import avatarInitials from '../helpers/avatar-initials';
import avatarColor from '../helpers/avatar-color';
import type { Mention } from '../schemas/mention.ts';
import { modifier } from 'ember-modifier';

// Modifier to add highlight classes and remove them after 5s
const autoHighlight = modifier(
  (
    element: Element,
    _positional: unknown[],
    named: Record<string, unknown>
  ) => {
    const shouldHighlight = named.shouldHighlight as boolean;
    if (!shouldHighlight) return;

    const htmlElement = element as HTMLElement;
    const highlightClass = 'bg-primary/20';

    htmlElement.classList.add(highlightClass);

    const timer = setTimeout(() => {
      htmlElement.classList.remove(highlightClass);
    }, 5000);

    return () => clearTimeout(timer);
  }
);

interface MentionCardSignature {
  Args: {
    mention: Mention;
    hideItem?: boolean;
    highlighted?: boolean;
  };
}

export default class MentionCard extends Component<MentionCardSignature> {
  get itemRecord(): Mention['item'] | undefined {
    return this.args.mention.item ?? undefined;
  }

  get containerClasses(): string {
    return tw('chat chat-start');
  }

  get sourceRecord(): Mention['contentSource'] | undefined {
    return this.args.mention.contentSource ?? undefined;
  }

  get hasItem(): boolean {
    return !!this.itemRecord;
  }

  get sourceId(): string | null {
    return this.sourceRecord?.id ?? null;
  }

  get sourceTitle(): string {
    return this.sourceRecord?.title ?? this.sourceRecord?.url ?? '';
  }

  get creatorId(): string | null {
    return this.sourceRecord?.creator?.id ?? null;
  }

  get creatorName(): string {
    return (
      this.sourceRecord?.creator?.displayName ??
      this.sourceRecord?.creator?.username ??
      ''
    );
  }

  get creatorAvatarPath(): string | null {
    return this.sourceRecord?.creator?.avatarPath ?? null;
  }

  get postedAt(): string | null {
    return this.sourceRecord?.postedAt ?? null;
  }

  get quote(): string | null {
    return this.args.mention.quote;
  }

  get summary(): string | null {
    return this.args.mention.summary;
  }

  get intent(): string | null {
    return this.args.mention.intent;
  }

  get sentiment(): number | null {
    return this.args.mention.sentiment;
  }

  get sentimentPolarity(): string | null {
    return this.args.mention.sentimentPolarity;
  }

  get sentimentLabel(): string | null {
    return this.args.mention.sentimentLabel;
  }

  get itemName(): string {
    if (!this.itemRecord) return '';
    const name = field(this.itemRecord, 'name');
    return typeof name === 'string' ? name : '';
  }

  get itemType(): string {
    if (!this.itemRecord) return '';
    const type = field(this.itemRecord, 'type');
    return typeof type === 'string' ? type : '';
  }

  get itemEmoji(): string {
    if (!this.itemRecord) return '';
    if (this.itemType !== 'place') return '';
    const emoji = field(this.itemRecord, 'emoji');
    return typeof emoji === 'string' ? emoji : '';
  }

  get mentionTimestamp(): number | null {
    return this.args.mention.mentionTimestamp ?? null;
  }

  get sourceUrl(): string | null {
    return this.sourceRecord?.url ?? null;
  }

  get formattedTimestamp(): string | null {
    const ts = this.mentionTimestamp;
    if (ts === null || ts === undefined) return null;
    return formatTimestamp(ts);
  }

  get sourcePlatform(): string | null {
    return this.sourceRecord?.platform ?? null;
  }

  get timestampUrl(): string | null {
    const ts = this.mentionTimestamp;
    const url = this.sourceUrl;
    if (ts === null || ts === undefined || !url) return null;
    if (this.sourcePlatform !== 'youtube') return null;
    return buildYouTubeTimestampUrl(url, ts);
  }

  <template>
    <div id="mention-{{@mention.id}}" class={{this.containerClasses}}>
      {{! Avatar: real image or generated initials }}
      <div
        class="chat-image avatar
          {{unless this.creatorAvatarPath 'placeholder'}}"
      >
        {{#if this.creatorAvatarPath}}
          <div class="w-10 rounded-full">
            <img
              src={{this.creatorAvatarPath}}
              alt={{this.creatorName}}
              loading="lazy"
              class="rounded-full"
            />
          </div>
        {{else}}
          <div
            class={{tw
              (avatarColor this.creatorId)
              "text-base-100 flex w-10 items-center justify-center rounded-full text-sm font-medium"
            }}
          >
            <span>{{avatarInitials this.creatorName}}</span>
          </div>
        {{/if}}
      </div>

      {{! Header: creator name + source title + timestamp }}
      <div class="chat-header">
        {{#if this.creatorId}}
          <span class="font-medium">
            {{this.creatorName}}
          </span>
        {{else}}
          <span class="opacity-70">Unknown</span>
        {{/if}}
        {{#if this.sourceId}}
          <span class="text-xs opacity-50"> on </span>
          <LinkTo
            @route="sources.show"
            @model={{this.sourceId}}
            class="link link-hover link-xs opacity-70"
          >
            {{truncate (or this.sourceTitle "") 30}}
          </LinkTo>
        {{/if}}
        {{#if this.postedAt}}
          <time class="text-xs opacity-50">
            {{formatDate (asDateValue this.postedAt)}}
          </time>
        {{/if}}
      </div>

      {{! Bubble: quote + summary + embedded item preview }}
      <div class="chat-bubble" {{autoHighlight shouldHighlight=@highlighted}}>
        {{! Badges + sentiment + summary (same line) }}
        <div class="mb-2 flex flex-wrap items-center gap-2 text-xs">
          {{#if this.intent}}
            <span
              class={{tw "badge badge-xs" (intentBadge this.intent)}}
            >{{this.intent}}</span>
          {{/if}}
          {{#if (hasSentiment this.sentiment)}}
            <span title={{sentimentTitle this.sentimentLabel this.sentiment}}>
              {{sentimentEmoji this.sentimentPolarity}}
            </span>
          {{/if}}
          {{#if this.summary}}
            <span class="opacity-80">{{this.summary}}</span>
          {{/if}}
        </div>

        {{#if this.quote}}
          <p class="italic opacity-90">{{this.quote}}</p>
        {{/if}}

        {{! Embedded item preview (at bottom, like attachment) }}
        {{#if (and this.hasItem (not @hideItem))}}
          {{#if this.itemRecord}}
            <LinkTo
              @route="items.show"
              @model={{this.itemRecord.id}}
              class="bg-base-300/50 hover:bg-base-300/70 mt-3 block overflow-hidden rounded-lg transition"
            >
              <div class="flex gap-3 p-2">
                {{! Item thumbnail }}
                <div
                  class="bg-base-100 h-16 w-12 shrink-0 overflow-hidden rounded"
                >
                  <img
                    src={{posterUrl (recordAsMap this.itemRecord)}}
                    alt=""
                    class="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                {{! Item info }}
                <div class="flex min-w-0 flex-col justify-center">
                  <span class="truncate font-medium">
                    {{#if this.itemEmoji}}
                      <span aria-hidden="true">{{this.itemEmoji}}</span>
                    {{/if}}
                    {{this.itemName}}
                  </span>
                  <span class="text-xs opacity-60">{{this.itemType}}</span>
                </div>
              </div>
            </LinkTo>
          {{/if}}
        {{/if}}
      </div>

      {{! Footer: timestamp (left) + actions (right) }}
      <div class="chat-footer">
        <div class="flex w-full items-center justify-between gap-1 text-xs">
          {{! Timestamp deep-link (YouTube) or plain text (other platforms) }}
          <div>
            {{#if this.formattedTimestamp}}
              {{#if this.timestampUrl}}
                <a
                  href={{this.timestampUrl}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="link link-hover opacity-60"
                >⏱ {{this.formattedTimestamp}}</a>
              {{else}}
                <span class="opacity-50">⏱ at {{this.formattedTimestamp}}</span>
              {{/if}}
            {{/if}}
          </div>
          {{! Action buttons }}
          <div class="flex gap-1">
            <FlagButton
              @targetType="mention"
              @targetId={{@mention.id}}
              @placement="dropdown-right"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
}

function intentBadge(intent: unknown): string {
  if (intent === 'opinion') return 'badge-primary';
  if (intent === 'contextual') return 'badge-ghost';
  if (intent === 'ambiguous') return 'badge-warning';
  return 'badge-outline';
}

function sentimentEmoji(val: unknown): string {
  const polarity = (val as string | null | undefined) ?? 'unknown';
  if (polarity === 'positive') return '👍';
  if (polarity === 'negative') return '👎';
  if (polarity === 'neutral') return '😐';
  return '❔';
}

function sentimentTitle(label: unknown, val: unknown): string {
  const base = (label as string | null | undefined) ?? 'Unknown';
  const n = Number(val);
  if (Number.isNaN(n)) return base;
  const pct = (n * 100).toFixed(0) + '%';
  return `${base} (${pct})`;
}

function hasSentiment(val: unknown): boolean {
  return val !== null && val !== undefined;
}

function formatTimestamp(seconds: number): string {
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function buildYouTubeTimestampUrl(url: string, seconds: number): string | null {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('t', String(Math.floor(seconds)));
    return parsed.toString();
  } catch {
    return null;
  }
}
